import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Book, Beaker, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

const CreateStudyBuilder = () => {
  const navigate = useNavigate();
  const [studyName, setStudyName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!studyName.trim()) {
      toast.error('Please enter a study name');
      return;
    }

    setLoading(true);
    try {
      const response = await studyBuilderService.createStudyDraft(studyName);
      toast.success('Study draft created successfully');
      navigate(`/studybuilder/${encodeURIComponent(studyName)}`);
    } catch (error) {
      const data = error.response?.data;

      // The backend returns descriptive messages in a few shapes: a field-keyed
      // `errors` dict (serializer validation, values may be a string or array),
      // or a flat `message`/`error`/`detail`/`details` string (missing fields,
      // sponsor errors, server errors). Pull out the most specific one.
      let detail;
      if (data?.errors && typeof data.errors === 'object') {
        const firstFieldError = Object.values(data.errors)[0];
        detail = Array.isArray(firstFieldError) ? firstFieldError[0] : firstFieldError;
      }
      detail = detail || data?.message || data?.error || data?.detail || data?.details;

      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error(detail || 'A study with this name already exists');
      } else if (error.response?.status === 400) {
        toast.error(detail || 'Invalid study data provided');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to create studies');
      } else {
        toast.error(detail || error.message || 'Failed to create the study. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link
            to="/studybuilder"
            className="group flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:transform group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Study Builder</span>
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 bg-primary-600 flex items-center">
            <Beaker className="w-6 h-6 text-white mr-3" />
            <h2 className="text-xl font-semibold text-white">Create New Study</h2>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="max-w-lg mx-auto">
              {/* Introduction */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 text-primary-600 rounded-full mb-4">
                  <Beaker className="w-8 h-8" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Let's Build Your Study</h1>
                <p className="text-gray-600 dark:text-gray-300">
                  Start by giving your study a name. You'll be able to configure all the details in the next steps.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <label htmlFor="studyName" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Study Name<span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Book className="text-gray-400 dark:text-gray-500" size={18} />
                    </div>
                    <input
                      type="text"
                      id="studyName"
                      value={studyName}
                      onChange={(e) => setStudyName(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow duration-200 bg-white dark:bg-gray-800 dark:text-gray-100"
                      placeholder="Enter study name"
                      required
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Choose a descriptive name that clearly identifies your research study
                  </p>
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <button
                    type="button"
                    onClick={() => navigate('/studybuilder')}
                    className="px-5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 font-medium flex items-center"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-5 py-2.5 rounded-lg shadow-sm dark:shadow-gray-900/20 text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center ${
                      loading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader className="animate-spin h-5 w-5 mr-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Study'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStudyBuilder;